import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
const EASE = [0.23, 1, 0.32, 1] as const;
type Size = 'sm' | 'md' | 'lg' | 'xl';
const WIDTH: Record<Size, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl'
};
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: Size;
  /** Sticky action row at the bottom. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Centred dialog used across the Pro workspace. Rises from the bottom on
 * phones, where a centred box would sit awkwardly above the keyboard.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  children
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-469" data-mp="2hggqkmcto6pd">
      {isOpen && <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" data-id="element-470" data-mp="2hfp4c2547lbr">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} onClick={onClose} className="absolute inset-0 bg-stone-900/40" data-id="element-471" data-mp="28h3mk97gwac1" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className={`relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl ${WIDTH[size]}`} data-id="element-472" data-mp="2wcpjl664dntc">
            <header className="flex items-start justify-between gap-4 border-b border-stone-200 px-5 py-4" data-id="element-473" data-mp="6v7q0y9y9tyd">
              <div className="min-w-0" data-id="element-474" data-mp="bry4xia0qji7">
                <h2 className="truncate text-lg font-bold tracking-tight text-stone-900" data-id="element-475" data-mp="bv3sgt301dbt">
                  {title}
                </h2>
                {subtitle && <p className="mt-0.5 text-sm text-stone-500" data-id="element-476" data-mp="21wnh9c67zc9">{subtitle}</p>}
              </div>
              <button onClick={onClose} aria-label="Luk" className="-mr-1 flex-shrink-0 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-477" data-mp="3kb5mn21a1y54">
                <XIcon className="h-5 w-5" data-id="element-478" data-mp="3f9gafj2bjefg" />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5" data-id="element-479" data-mp="19haz08iv5fqt">
              {children}
            </div>

            {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-stone-200 bg-stone-50 px-5 py-4" data-id="element-480" data-mp="1dhbjoawk2n6b">
                {footer}
              </footer>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}